import { useState } from "react"
import { Modal, Platform, Pressable, View } from "react-native"
import { SafeAreaView } from "react-native-safe-area-context"
import { Show, useObservable } from "@legendapp/state/react"
import { useDebounce } from "use-debounce"

import { ar } from "@src/i18n"
import { cn, sharedStyles, trpc } from "@src/utils"
import { ActivityIndicator, Text, TextInput, MCIcon } from "./core"
import { Header } from "./Header"
import { List } from "./list"

type Props = { id?: number; onChange: (id?: number) => void; className?: string }

export function ExpenseCategoryPicker({ id, className, onChange }: Props) {
  const modal$ = useObservable(false)
  const categories = trpc.expenseCategory.list.useQuery()
  const selected = categories.data?.items.find((c) => c.id === id)

  return (
    <>
      <Pressable className={cn("min-h-[45px] bg-gray-50 border border-gray-300 rounded px-4 justify-center active:opacity-50", className)} onPress={() => modal$.set(true)}>
        <View className="flex-row items-center gap-2">
          {selected?.color && <View className="w-3 h-3 rounded-full" style={{ backgroundColor: selected.color }} />}
          <Text className={selected ? "text-gray-900" : "text-gray-500"}>{selected?.name ?? ar.selectCategory}</Text>
        </View>
      </Pressable>

      <Show if={modal$}>{() => <CategoryPickModal selectedId={id} onSelectionChange={onChange} hide={() => modal$.set(false)} />}</Show>
    </>
  )
}

function CategoryPickModal({ selectedId, onSelectionChange, hide }: { selectedId?: number; onSelectionChange: (id?: number) => void; hide: () => void }) {
  const [search, setSearch] = useState("")
  const [debouncedSearch] = useDebounce(search, 300)

  const categories = trpc.expenseCategory.list.useQuery()
  const filtered = categories.data?.items.filter((c) => c.name.toLowerCase().includes(debouncedSearch.toLowerCase()))

  const header = (
    <Header
      title={ar.expenseCategories}
      withoutInsets={Platform.OS === "ios"}
      right={
        <Pressable onPress={hide} className="h-full px-2 justify-center">
          <MCIcon name="close" size={25} />
        </Pressable>
      }
    />
  )

  if (categories.isLoading) {
    return (
      <>
        {header}
        <ActivityIndicator className="pt-4" />
      </>
    )
  }

  return (
    <Modal animationType="slide" presentationStyle="formSheet" allowSwipeDismissal onDismiss={hide} onRequestClose={hide}>
      {header}

      <View className="w-full max-w-content mx-auto px-2 mb-2">
        <TextInput placeholder={ar.selectCategory} value={search} onChangeText={setSearch} />
      </View>

      <List
        contentContainerStyle={sharedStyles.contentContainer}
        data={filtered}
        keyExtractor={(item) => item.id.toString()}
        ListEmptyComponent={<Text className="text-gray-500 text-center pt-4">{ar.noCategories}</Text>}
        renderItem={({ item }) => {
          const isSelected = item.id === selectedId
          return (
            <Pressable
              className="py-3 px-4 border-b border-gray-200 active:opacity-70"
              onPress={() => {
                onSelectionChange(item.id)
                hide()
              }}
            >
              <View className="flex-row items-center gap-2">
                {item.color && <View className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />}
                <Text className={isSelected ? "text-blue-600 font-bold" : "text-gray-900"}>{item.name}</Text>
                {isSelected && <MCIcon name="check" size={18} color="#3B82F6" />}
              </View>
            </Pressable>
          )
        }}
        ListFooterComponent={<SafeAreaView edges={["bottom"]} />}
      />
    </Modal>
  )
}
